import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useCart } from '@/cart/useCart';
import {
  Users, CheckCircle2, Wallet, Search,
  Plus, Upload, Download, Pencil, Trash2, MessageSquarePlus, Eye, ChevronDown,
} from '@/components/icons';
import {
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, LabelList, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { listUsers } from '@/api/admin';
import {
  listEnquiries, createEnquiry, updateEnquiry, deleteEnquiry as apiDeleteEnquiry, addEnquiryComm,
  listEnqTeam, addEnqTeamMember, deleteEnqTeamMember, enquiriesExportPath,
  type EnqPayload, type EnqCommPayload, type EnqTeamMember,
} from '@/api/enquiryDash';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatINR } from '@/lib/format';
import { EnquiryFormModal } from './EnquiryFormModal';
import { EnquiryCommLogModal } from './EnquiryCommLogModal';
import { EnquiryViewModal } from './EnquiryViewModal';
import {
  ENQ_CATEGORIES, ENQ_CHANNELS, ENQ_STATUSES,
  filterByRange, computeStats, categoryBreakdown, personStats, byChannel, byMonth, accountsRollup,
  firstContact, lastUpdate, commCount, uid,
  type Enquiry, type EnqComm, type EnqStatus, type RangeKey, type AccountRow, type EnqItem,
} from './enquiryDomain';

const STATUS_TONE: Record<EnqStatus, 'success' | 'danger' | 'warning' | 'info' | 'accent' | 'muted'> = {
  'Enquiry Received': 'muted',
  'Pending Sales Head Approval': 'warning',
  'Approved — Stock Check': 'info',
  'Sales Order Generated': 'success',
  'Rejected by Sales Head': 'danger',
  'Rejected — Out of Stock': 'danger',
};
const RANGES: [RangeKey, string][] = [['week', 'This week'], ['month', 'This month'], ['year', 'This year'], ['all', 'All time']];
const TABS = [['overview', 'Overview'], ['inquiries', 'Enquiries'], ['team', 'Team'], ['accounts', 'Accounts']] as const;
type TabKey = (typeof TABS)[number][0];

const chartTooltip = { background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' };

function Panel({ title, subtitle, action, children }: { title: string; subtitle?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)]">
      <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-4">
        <div>
          <h3 className="text-sm font-extrabold text-[var(--text-primary)]">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[11px] text-[var(--text-muted)]">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </section>
  );
}
const Empty = ({ children }: { children: ReactNode }) => (
  <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{children}</div>
);

export function EnquiryDashboardPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const enqQuery = useQuery({ queryKey: ['enquiries'], queryFn: listEnquiries });
  const rows = enqQuery.data ?? [];
  const teamQuery = useQuery({ queryKey: ['enq-team'], queryFn: listEnqTeam });
  const team = useMemo<EnqTeamMember[]>(() => teamQuery.data ?? [], [teamQuery.data]);
  const people = useMemo(() => team.map((t) => t.name), [team]);
  const [tab, setTab] = useState<TabKey>('overview');
  const [range, setRange] = useState<RangeKey>('all');

  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [chFilter, setChFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Enquiry | null>(null);
  const [commRow, setCommRow] = useState<Enquiry | null>(null);
  const [viewing, setViewing] = useState<Enquiry | null>(null);
  const [newPerson, setNewPerson] = useState('');
  const [presetItems, setPresetItems] = useState<EnqItem[] | undefined>(undefined);

  // Arriving from the catalogue cart (?fromCart=1) → open "Log enquiry"
  // pre-filled with the cart lines, then consume the cart + the URL flag.
  const [searchParams, setSearchParams] = useSearchParams();
  const cart = useCart();
  useEffect(() => {
    if (searchParams.get('fromCart') !== '1' || cart.isLoading) return;
    const items: EnqItem[] = (cart.cart?.items ?? []).map((it) => ({
      id: uid(), itemId: it.itemId, item: it.item.name, qty: it.qty, unit: it.item.unit, rate: it.item.mrp,
    }));
    setPresetItems(items);
    setEditing(null);
    setFormOpen(true);
    setTab('inquiries');
    const next = new URLSearchParams(searchParams);
    next.delete('fromCart');
    setSearchParams(next, { replace: true });
    void cart.clear();
  }, [searchParams, cart.isLoading, cart.cart, setSearchParams, cart]);

  const filtered = useMemo(() => filterByRange(rows, range), [rows, range]);

  // Team members are sourced from User Management — active Sales Managers and
  // Sales Executives. The Team tab's dropdown fetches from here.
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: listUsers, staleTime: 60_000 });
  const salesStaff = useMemo(
    () =>
      (usersQuery.data ?? [])
        .filter((u) => (u.roleCode === 'SALES_MGR' || u.roleCode === 'SALES_EXEC') && u.status === 'Active')
        .map((u) => ({ name: u.fullName, role: u.role })),
    [usersQuery.data],
  );

  // ---- mutations (backed by the /enquiry-dashboard API) ----
  const invalidate = () => qc.invalidateQueries({ queryKey: ['enquiries'] });
  const onErr = (e: unknown) => toast.push(apiError(e, 'Something went wrong'), 'error');
  const createMut = useMutation({ mutationFn: createEnquiry, onSuccess: () => { invalidate(); toast.push('Enquiry saved', 'success'); }, onError: onErr });
  const updateMut = useMutation({ mutationFn: ({ id, payload }: { id: number; payload: EnqPayload }) => updateEnquiry(id, payload), onSuccess: () => { invalidate(); toast.push('Enquiry updated', 'success'); }, onError: onErr });
  const deleteMut = useMutation({ mutationFn: apiDeleteEnquiry, onSuccess: () => { invalidate(); toast.push('Enquiry deleted', 'success'); }, onError: onErr });
  const commMut = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: EnqCommPayload }) => addEnquiryComm(id, payload),
    onSuccess: (updated) => { invalidate(); setCommRow((cur) => (cur && cur.id === updated.id ? updated : cur)); toast.push('Communication logged', 'success'); },
    onError: onErr,
  });
  const invalidateTeam = () => qc.invalidateQueries({ queryKey: ['enq-team'] });
  const addTeamMut = useMutation({ mutationFn: addEnqTeamMember, onSuccess: () => { invalidateTeam(); toast.push('Team member saved', 'success'); }, onError: onErr });
  const deleteTeamMut = useMutation({ mutationFn: deleteEnqTeamMember, onSuccess: () => { invalidateTeam(); toast.push('Team member removed', 'success'); }, onError: onErr });

  function toPayload(row: Enquiry): EnqPayload {
    // Once an enquiry has entered the pipeline its items are locked (the
    // backend rejects item edits past stage 'new'), so omit them from the
    // payload — otherwise even a notes-only save would be refused.
    const opsControlled = !!row.stage && row.stage !== 'new';
    return {
      partyId: row.partyId ?? 0,
      date: row.date, category: row.category, channel: row.channel, assignedTo: row.assignedTo,
      status: row.status, salesOrderNo: row.salesOrderNo, approvedBy: row.approvedBy, notes: row.notes,
      ...(opsControlled ? {} : {
        items: row.items.map((it) => ({ itemId: it.itemId, item: it.item, qty: it.qty, unit: it.unit, rate: it.rate })),
      }),
    };
  }
  // New rows carry a client uid (non-numeric); existing ones a numeric Enquiry id.
  function saveRow(row: Enquiry) {
    if (/^\d+$/.test(row.id)) updateMut.mutate({ id: Number(row.id), payload: toPayload(row) });
    else createMut.mutate(toPayload(row));
  }
  function deleteRow(id: string) { deleteMut.mutate(Number(id)); }
  function addComm(rowId: string, comm: EnqComm) {
    commMut.mutate({ id: Number(rowId), payload: { date: comm.date, type: comm.type, by: comm.by, note: comm.note } });
  }
  function addPerson() {
    const name = newPerson.trim();
    if (name && !people.includes(name)) { addTeamMut.mutate(name); setNewPerson(''); }
  }
  function removePerson(id: number) { deleteTeamMut.mutate(id); }

  return (
    <div>
      <SectionHeader title="Enquiry Dashboard" />

      {/* Tabs + period */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-x-4 gap-y-3 border-b border-[var(--border-color)]">
        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {TABS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-bold transition ${
                tab === k ? 'border-accent text-accent' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="mb-2"><Pills options={RANGES} value={range} onChange={(v) => setRange(v as RangeKey)} /></div>
      </div>

      {tab === 'overview' && <Overview rows={filtered} people={people} rangeLabel={RANGES.find(([k]) => k === range)?.[1] ?? ''} />}
      {tab === 'inquiries' && (
        <InquiriesTab
          rows={filtered} search={search} setSearch={setSearch}
          catFilter={catFilter} setCatFilter={setCatFilter}
          chFilter={chFilter} setChFilter={setChFilter}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          onNew={() => { setEditing(null); setPresetItems(undefined); setFormOpen(true); }}
          onEdit={(r) => { setEditing(r); setFormOpen(true); }}
          onDelete={deleteRow}
          onLog={(r) => setCommRow(r)}
          onView={(r) => setViewing(r)}
          onExport={async () => {
            try { await downloadExcel(enquiriesExportPath(), 'GRHAMOY_Enquiries.xlsx', 5 * 60 * 1000); }
            catch (e) { toast.push(apiError(e, 'Export failed'), 'error'); }
          }}
          onImport={() => toast.push('Excel import will be enabled soon.', 'info')}
        />
      )}
      {tab === 'team' && (
        <TeamTab rows={filtered} team={team} salesStaff={salesStaff} loadingStaff={usersQuery.isLoading} newPerson={newPerson} setNewPerson={setNewPerson} addPerson={addPerson} onDelete={removePerson} />
      )}
      {tab === 'accounts' && <AccountsTab rows={filtered} />}

      <EnquiryFormModal open={formOpen} editing={editing} people={people} presetItems={presetItems} onClose={() => { setFormOpen(false); setPresetItems(undefined); }} onSave={saveRow} />
      <EnquiryCommLogModal open={commRow !== null} row={commRow} people={people} onClose={() => setCommRow(null)} onAdd={addComm} />
      <EnquiryViewModal open={viewing !== null} row={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

function Pills({ options, value, onChange }: { options: readonly (readonly [string, string])[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex w-fit gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1">
      {options.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-bold transition sm:px-3.5 ${
            value === k ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-secondary)] hover:text-accent'
          }`}>
          {label}
        </button>
      ))}
    </div>
  );
}

// ---------- Overview ----------
// Status colours used across the overview (bars, dots, figures). Pending is blue
// (in progress) so it never reads as the brand orange used for volumes.
const STATUS_COLOR = { pending: '#3b82f6', so: '#22c55e', rejected: '#ef4444' } as const;
const pct = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);
// One fixed colour per channel (by its place in ENQ_CHANNELS), so a channel
// keeps its colour whatever the period or ranking.
const CHANNEL_COLORS = ['#FF8C00', '#14b8a6', '#6366f1', '#ec4899', '#a855f7', '#0ea5e9', '#84cc16', '#64748b'];
const channelColor = (name: string) => CHANNEL_COLORS[Math.max(0, (ENQ_CHANNELS as readonly string[]).indexOf(name)) % CHANNEL_COLORS.length];
/** "2026-07" → "Jul 26" for the month chart. */
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function monthLabel(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  return m ? `${MONTH_SHORT[Number(m[2]) - 1]} ${m[1].slice(2)}` : ym;
}

/** Pending / SO generated / rejected as one horizontal stacked bar. Empty → a plain track. */
function StatusBar({ pending, so, rejected, height = 8 }: { pending: number; so: number; rejected: number; height?: number }) {
  const total = pending + so + rejected;
  const parts = [['pending', 'Pending', pending], ['so', 'SO generated', so], ['rejected', 'Rejected', rejected]] as const;
  return (
    <div className="flex w-full overflow-hidden rounded-full bg-[var(--bg-secondary)]" style={{ height, gap: total ? 2 : 0 }}>
      {total > 0 && parts.map(([k, label, v]) => v > 0 && (
        <div key={k} title={`${label}: ${v}`} style={{ width: `${(v / total) * 100}%`, background: STATUS_COLOR[k] }} />
      ))}
    </div>
  );
}
function Dot({ color }: { color: string }) {
  return <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />;
}

function Overview({ rows, people, rangeLabel }: { rows: Enquiry[]; people: string[]; rangeLabel: string }) {
  const stats = computeStats(rows);
  const cats = categoryBreakdown(rows);
  const channels = byChannel(rows).sort((a, b) => b.value - a.value);
  const months = byMonth(rows);
  const team = people.map((p) => personStats(rows, p));

  const statusParts = [
    { key: 'pending', label: 'Pending', value: stats.pendingCount, color: STATUS_COLOR.pending },
    { key: 'so', label: 'SO generated', value: stats.soCount, color: STATUS_COLOR.so },
    { key: 'rejected', label: 'Rejected', value: stats.rejectedCount, color: STATUS_COLOR.rejected },
  ];

  return (
    <div className="space-y-5">
      {/* Summary — total, how it splits by status, and what has turned into sales orders. */}
      <section className="grid overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] md:grid-cols-[minmax(150px,auto)_1fr_minmax(200px,auto)]">
        <div className="flex items-center gap-3 border-b border-[var(--border-color)] p-5 md:block md:border-b-0 md:border-r">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent md:mb-3"><Users size={18} /></span>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Total enquiries</div>
            <div className="text-3xl font-extrabold tabular-nums text-[var(--text-primary)]">{stats.total}</div>
            <div className="text-[11px] text-[var(--text-muted)]">{rangeLabel}</div>
          </div>
        </div>

        <div className="border-b border-[var(--border-color)] p-5 md:border-b-0 md:border-r">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Where the enquiries stand</div>
          <StatusBar pending={stats.pendingCount} so={stats.soCount} rejected={stats.rejectedCount} height={12} />
          <div className="mt-4 grid grid-cols-3 gap-3">
            {statusParts.map((s) => (
              <div key={s.key}>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[var(--text-secondary)]"><Dot color={s.color} />{s.label}</div>
                <div className="mt-1 text-xl font-extrabold tabular-nums" style={{ color: s.value ? s.color : 'var(--text-muted)' }}>{s.value}</div>
                <div className="text-[11px] tabular-nums text-[var(--text-muted)]">{pct(s.value, stats.total)}% of enquiries</div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-1">
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]"><Wallet size={13} /> Sales order value</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums" style={{ color: stats.totalValue ? STATUS_COLOR.so : 'var(--text-primary)' }}>₹{formatINR(stats.totalValue) || 0}</div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]"><CheckCircle2 size={13} /> Conversion rate</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums text-[var(--text-primary)]">{stats.conversionRate}%</div>
          </div>
        </div>
      </section>

      <div className="grid gap-5 md:grid-cols-2">
        <Panel title="Enquiries by channel">
          {channels.length === 0 ? <Empty>No enquiries in this period</Empty> : (
            <div className="flex flex-col items-center gap-5 sm:flex-row">
              {/* Donut with the period total in the middle. */}
              <div className="relative h-[180px] w-[180px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={channels} dataKey="value" nameKey="name" innerRadius={58} outerRadius={84} paddingAngle={channels.length > 1 ? 3 : 0} cornerRadius={channels.length > 1 ? 4 : 0} stroke="none" startAngle={90} endAngle={-270}>
                      {channels.map((c) => <Cell key={c.name} fill={channelColor(c.name)} />)}
                    </Pie>
                    <Tooltip contentStyle={chartTooltip} formatter={(v, n) => [`${v} (${pct(Number(v), stats.total)}%)`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-extrabold tabular-nums text-[var(--text-primary)]">{stats.total}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">enquiries</span>
                </div>
              </div>
              <ul className="w-full min-w-0 flex-1 space-y-2.5">
                {channels.map((c) => (
                  <li key={c.name} className="flex items-center gap-2.5 text-xs">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: channelColor(c.name) }} />
                    <span className="min-w-0 flex-1 truncate font-semibold text-[var(--text-primary)]">{c.name}</span>
                    <span className="font-extrabold tabular-nums text-[var(--text-primary)]">{c.value}</span>
                    <span className="w-10 text-right tabular-nums text-[var(--text-muted)]">{pct(c.value, stats.total)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>

        <Panel title="Enquiries by month">
          {months.length === 0 ? <Empty>No enquiries in this period</Empty> : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={months} margin={{ top: 22, right: 16, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id="enqMonthFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF8C00" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FF8C00" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(128,128,128,0.15)" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} padding={{ left: 12, right: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={chartTooltip} cursor={{ stroke: 'var(--border-color)' }} labelFormatter={(m) => monthLabel(String(m))} />
                <Area
                  type="monotone" dataKey="inquiries" name="Enquiries" stroke="#FF8C00" strokeWidth={2.5} fill="url(#enqMonthFill)"
                  dot={{ r: 4, fill: '#FF8C00', stroke: 'var(--card-bg)', strokeWidth: 2 }} activeDot={{ r: 6, stroke: 'var(--card-bg)', strokeWidth: 2 }}
                >
                  <LabelList dataKey="inquiries" position="top" offset={10} style={{ fontSize: 10, fontWeight: 700, fill: 'var(--text-secondary)' }} />
                </Area>
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <Panel title="Pending & sales-order conversion by category">
        {rows.length === 0 ? <Empty>No enquiries in this period</Empty> : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full min-w-[540px] text-sm">
              <thead>
                <tr className="border-y border-[var(--border-color)] bg-[var(--bg-secondary)] text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="px-5 py-2.5 text-left">Category</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                  <th className="px-3 py-2.5 text-right"><span className="inline-flex items-center gap-1.5"><Dot color={STATUS_COLOR.pending} />Pending</span></th>
                  <th className="px-3 py-2.5 text-right"><span className="inline-flex items-center gap-1.5"><Dot color={STATUS_COLOR.so} />SO generated</span></th>
                  <th className="px-3 py-2.5 text-right"><span className="inline-flex items-center gap-1.5"><Dot color={STATUS_COLOR.rejected} />Rejected</span></th>
                  <th className="px-5 py-2.5 text-right">Conv. rate</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {cats.map((c) => {
                  // Zeros fade so the categories with activity stand out.
                  const cell = (v: number, color = 'var(--text-primary)') => ({ color: v ? color : 'var(--text-muted)', fontWeight: v ? 700 : 400 });
                  return (
                    <tr key={c.category} className="border-b border-[var(--border-color)] last:border-b-0 hover:bg-[var(--bg-secondary)]">
                      <td className="px-5 py-3 font-semibold text-[var(--text-primary)]">{c.category}</td>
                      <td className="px-3 py-3 text-right" style={cell(c.total)}>{c.total}</td>
                      <td className="px-3 py-3 text-right" style={cell(c.pending, STATUS_COLOR.pending)}>{c.pending}</td>
                      <td className="px-3 py-3 text-right" style={cell(c.so, STATUS_COLOR.so)}>{c.so}</td>
                      <td className="px-3 py-3 text-right" style={cell(c.rejected, STATUS_COLOR.rejected)}>{c.rejected}</td>
                      <td className="px-5 py-3 text-right" style={cell(c.rate, STATUS_COLOR.so)}>{c.rate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Enquiries handled by team member">
        {team.length === 0 ? <Empty>No team members yet — add them in the Team tab</Empty> : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {team.map((p) => (
              <div key={p.name} className="rounded-lg border border-[var(--border-color)] p-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/10 text-xs font-extrabold text-accent">
                    {p.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-[var(--text-primary)]">{p.name}</div>
                    <div className="text-[11px] text-[var(--text-muted)]">{p.orders} enquir{p.orders === 1 ? 'y' : 'ies'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-extrabold tabular-nums" style={{ color: p.rate ? STATUS_COLOR.so : 'var(--text-muted)' }}>{p.rate}%</div>
                    <div className="text-[10px] text-[var(--text-muted)]">conv. rate</div>
                  </div>
                </div>
                <div className="mt-3"><StatusBar pending={p.pending} so={p.so} rejected={Math.max(0, p.orders - p.pending - p.so)} height={6} /></div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                  <div><div className="text-[var(--text-muted)]">Pending</div><div className="font-bold tabular-nums" style={{ color: p.pending ? STATUS_COLOR.pending : 'var(--text-muted)' }}>{p.pending}</div></div>
                  <div><div className="text-[var(--text-muted)]">SO generated</div><div className="font-bold tabular-nums" style={{ color: p.so ? STATUS_COLOR.so : 'var(--text-muted)' }}>{p.so}</div></div>
                  <div className="text-right"><div className="text-[var(--text-muted)]">Sales done</div><div className="font-bold tabular-nums text-[var(--text-primary)]">₹{formatINR(p.salesValue) || 0}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ---------- Inquiries ----------
/** "2026-09-29" → "29 Sep" (this year) or "29 Sep 25" (other years). */
function fmtDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return iso;
  const year = Number(m[1]) === new Date().getFullYear() ? '' : ` ${m[1].slice(2)}`;
  return `${Number(m[3])} ${MONTH_SHORT[Number(m[2]) - 1]}${year}`;
}
/** "2026-09-29" → "29 Sep 2026" — the Date column always shows the full year. */
function fmtFullDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${Number(m[3])} ${MONTH_SHORT[Number(m[2]) - 1]} ${m[1]}` : iso;
}
const ACTION_BTN = 'inline-flex items-center gap-1 rounded-md p-1 text-[var(--text-muted)] hover:bg-accent/10 hover:text-accent xl:border xl:border-[var(--border-color)] xl:px-2 xl:py-1 xl:text-[11px] xl:font-semibold xl:text-[var(--text-secondary)] xl:hover:border-accent/40';
interface InquiriesTabProps {
  rows: Enquiry[];
  search: string; setSearch: (v: string) => void;
  catFilter: string; setCatFilter: (v: string) => void;
  chFilter: string; setChFilter: (v: string) => void;
  statusFilter: string; setStatusFilter: (v: string) => void;
  onNew: () => void; onEdit: (r: Enquiry) => void; onDelete: (id: string) => void; onLog: (r: Enquiry) => void; onView: (r: Enquiry) => void; onExport: () => void; onImport: () => void;
}
function InquiriesTab(p: InquiriesTabProps) {
  // Apply search here (not via the Table's search prop) so the count in the
  // table header reflects exactly the rows shown, not the pre-search total.
  const terms = p.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const rows = p.rows
    .filter((e) => (p.catFilter === 'All' || e.category === p.catFilter)
      && (p.chFilter === 'All' || e.channel === p.chFilter)
      && (p.statusFilter === 'All' || e.status === p.statusFilter))
    .filter((e) => {
      if (!terms.length) return true;
      const hay = [e.date, e.customer, e.category, e.channel, e.status, e.item, e.assignedTo, e.salesOrderNo, e.enqNo]
        .map((v) => String(v ?? '').toLowerCase()).join(' ');
      return terms.every((t) => hay.includes(t));
    })
    .sort((a, b) => b.date.localeCompare(a.date));

  // Client-side pagination over the filtered rows.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Snap back to page 1 whenever the filtered set changes, so we never sit on
  // an out-of-range (empty) page after a search/filter narrows the list.
  useEffect(() => { setPage(1); }, [p.search, p.catFilter, p.chFilter, p.statusFilter]);
  const total = rows.length;
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input value={p.search} onChange={(e) => p.setSearch(e.target.value)} placeholder="Search" className="!pl-5 sm:!pl-8" aria-label="Search enquiries" />
          </div>
        </FilterField>
        <FilterField label="Category">
          <Select value={p.catFilter} onChange={(e) => p.setCatFilter(e.target.value)}>
            <option>All</option>{ENQ_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Channel">
          <Select value={p.chFilter} onChange={(e) => p.setChFilter(e.target.value)}>
            <option>All</option>{ENQ_CHANNELS.map((c) => <option key={c}>{c}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Status">
          <Select value={p.statusFilter} onChange={(e) => p.setStatusFilter(e.target.value)}>
            <option>All</option>{ENQ_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </FilterField>
        <div className="col-span-2 flex flex-wrap items-end gap-2 sm:col-span-1 sm:ml-auto sm:self-end">
          <Button variant="glass" size="sm" onClick={p.onExport} icon={<Download size={13} />}>Export</Button>
          <Button variant="glass" size="sm" onClick={p.onImport} icon={<Upload size={13} />}>Import</Button>
          <Button variant="primary" size="sm" onClick={p.onNew} icon={<Plus size={13} />}>Log enquiry</Button>
        </div>
      </FilterBar>

      <Table<Enquiry>
        title="Enquiries"
        count={`${total} enquir${total !== 1 ? 'ies' : 'y'}`}
        rows={pageRows}
        rowKey={(e) => e.id}
        empty="No enquiries match these filters — log one to get started."
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
        // Compact layout (same as the Lead Dashboard): long text wraps instead of
        // widening its column, and the follow-up log is two short lines — so the
        // whole row fits without scrolling sideways. Every field is still shown.
        columns={[
          { key: 'date', header: 'Date', cell: (e) => <span className="tabular-nums" style={{ color: 'var(--text-secondary)' }}>{fmtFullDay(e.date)}</span> },
          {
            key: 'customer', header: 'Customer',
            cell: (e) => <div className="mx-auto min-w-[120px] max-w-[190px] whitespace-normal py-0.5 font-bold leading-snug" style={{ color: 'var(--text-primary)' }}>{e.customer}</div>,
          },
          {
            key: 'category', header: 'Customer Type',
            // Long types wrap inside the tag rather than widening the column.
            cell: (e) => (e.category
              ? <span className="inline-block max-w-[110px] whitespace-normal rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[11px] font-bold leading-tight text-accent">{e.category}</span>
              : <span style={{ color: 'var(--text-muted)' }}>—</span>),
          },
          {
            key: 'item', header: 'Item Qty',
            cell: (e) => <span className="tabular-nums" style={{ color: e.qty ? 'var(--text-primary)' : 'var(--text-muted)' }}>{e.qty || '—'}</span>,
          },
          { key: 'channel', header: 'Channel', cell: (e) => e.channel },
          { key: 'assigned', header: 'Assigned', cell: (e) => (e.assignedTo ? <div className="mx-auto max-w-[96px] whitespace-normal leading-snug">{e.assignedTo}</div> : <span style={{ color: 'var(--text-muted)' }}>—</span>) },
          {
            key: 'status', header: 'Status / SO#',
            cell: (e) => (
              <div className="mx-auto max-w-[150px] whitespace-normal xl:max-w-none xl:whitespace-nowrap">
                <Badge tone={STATUS_TONE[e.status]}>{e.status}</Badge>
                {e.salesOrderNo && <div className="mt-1"><Badge tone="success">{e.salesOrderNo}</Badge></div>}
                {e.approvedBy && <div className="mt-1 text-[11px] font-medium leading-tight" style={{ color: 'var(--text-muted)' }}>Approved: {e.approvedBy}</div>}
              </div>
            ),
          },
          {
            key: 'value', header: 'Order Value',
            cell: (e) => <span className="tabular-nums">{e.valueInr ? `₹${formatINR(e.valueInr)}` : <span style={{ color: 'var(--text-muted)' }}>—</span>}</span>,
          },
          {
            key: 'comm', header: 'Follow-ups', pickerLabel: 'Follow-ups (first → last)',
            cell: (e) => {
              const n = commCount(e);
              return (
                <div className="text-[11px] font-medium leading-4" style={{ color: 'var(--text-muted)' }} title={`First contact ${fmtFullDay(firstContact(e))} · last update ${fmtFullDay(lastUpdate(e))}`}>
                  <div><strong style={{ color: n ? 'var(--text-primary)' : 'var(--text-muted)' }}>{n}</strong> logged</div>
                  <div>{fmtDay(firstContact(e))} → {fmtDay(lastUpdate(e))}</div>
                </div>
              );
            },
          },
          {
            key: 'actions', header: 'Action', alwaysVisible: true,
            // Named buttons on wide screens; icons only (names on hover) where space is tight.
            cell: (e) => (
              <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                <button onClick={() => p.onLog(e)} className={ACTION_BTN} title="Log communication"><MessageSquarePlus size={13} /><span className="hidden xl:inline">Log</span></button>
                <button onClick={() => p.onView(e)} className={ACTION_BTN} title="View details"><Eye size={13} /><span className="hidden xl:inline">View</span></button>
                <button onClick={() => p.onEdit(e)} className={ACTION_BTN} title="Edit enquiry"><Pencil size={13} /><span className="hidden xl:inline">Edit</span></button>
                <button onClick={() => p.onDelete(e.id)} className="rounded-md p-1 text-[var(--text-muted)] hover:bg-red-500/10 hover:text-red-500" title="Delete" aria-label="Delete"><Trash2 size={14} /></button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

// ---------- Team ----------
const initials = (name: string) => name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

function TeamTab({ rows, team, salesStaff, loadingStaff, newPerson, setNewPerson, addPerson, onDelete }: { rows: Enquiry[]; team: EnqTeamMember[]; salesStaff: { name: string; role: string }[]; loadingStaff: boolean; newPerson: string; setNewPerson: (v: string) => void; addPerson: () => void; onDelete: (id: number) => void }) {
  const people = team.map((t) => t.name);
  const members = team.map((t) => ({
    ...t,
    stats: personStats(rows, t.name),
    // This member's enquiries within the selected period (rows is already
    // range-filtered), newest first — revealed when the card is expanded.
    enquiries: rows.filter((r) => r.assignedTo === t.name).sort((a, b) => b.date.localeCompare(a.date)),
  }));
  const [openId, setOpenId] = useState<number | null>(null);
  // Only offer staff who aren't already on the team.
  const available = salesStaff.filter((s) => !people.includes(s.name));
  return (
    <div className="space-y-4">
      <section className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-3">
        <div className="mr-auto">
          <div className="text-sm font-extrabold text-[var(--text-primary)]">Team members</div>
          <div className="text-[11px] text-[var(--text-muted)]">{members.length} on the team · add active Sales Managers / Executives from User Management</div>
        </div>
        <Select
          value={newPerson}
          onChange={(e) => setNewPerson(e.target.value)}
          className="w-full sm:w-72"
          disabled={loadingStaff}
        >
          <option value="">
            {loadingStaff
              ? 'Loading sales staff…'
              : available.length === 0
                ? (salesStaff.length === 0 ? 'No Sales Manager / Executive in User Management' : 'All sales staff already added')
                : '— Select Sales Manager / Executive —'}
          </option>
          {available.map((s) => (
            <option key={s.name} value={s.name}>{s.name} — {s.role}</option>
          ))}
        </Select>
        <Button variant="primary" size="sm" onClick={addPerson} icon={<Plus size={13} />} disabled={!newPerson}>Add member</Button>
      </section>

      {members.length === 0 ? (
        <Panel title="Team"><Empty>No team members yet. Add one above to start assigning enquiries.</Empty></Panel>
      ) : (
        <div className="grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {members.map((m) => {
            const open = openId === m.id;
            const s = m.stats;
            return (
              <div key={m.id} className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10 text-sm font-extrabold text-accent">{initials(m.name)}</span>
                  {/* Name — click to reveal this member's enquiries */}
                  <button type="button" onClick={() => setOpenId(open ? null : m.id)} className="min-w-0 flex-1 text-left" aria-expanded={open} title={open ? 'Hide enquiries' : 'Show enquiries'}>
                    <div className="flex items-center gap-1 text-[15px] font-extrabold text-[var(--text-primary)]">
                      <span className="truncate">{m.name}</span>
                      <ChevronDown size={15} className="shrink-0 text-[var(--text-muted)] transition-transform" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
                    </div>
                    <div className="text-[11px] text-[var(--text-muted)]">{s.orders} enquir{s.orders === 1 ? 'y' : 'ies'} handled</div>
                  </button>
                  <div className="text-right">
                    <div className="text-lg font-extrabold leading-none tabular-nums" style={{ color: s.rate ? STATUS_COLOR.so : 'var(--text-muted)' }}>{s.rate}%</div>
                    <div className="mt-1 text-[10px] text-[var(--text-muted)]">conv. rate</div>
                  </div>
                  <button type="button" onClick={() => onDelete(m.id)} className="-mr-1 rounded-md p-1 text-[var(--text-muted)] hover:bg-red-500/10 hover:text-red-500" title="Remove from team" aria-label="Remove from team">
                    <Trash2 size={15} />
                  </button>
                </div>

                <div className="mt-4"><StatusBar pending={s.pending} so={s.so} rejected={Math.max(0, s.orders - s.pending - s.so)} height={6} /></div>

                <div className="mt-4 grid grid-cols-3 divide-x divide-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] py-2.5 text-center">
                  <TeamFigure label="Pending" value={s.pending} color={STATUS_COLOR.pending} />
                  <TeamFigure label="SO generated" value={s.so} color={STATUS_COLOR.so} />
                  <TeamFigure label="Sales done" value={s.salesValue} money />
                </div>

                {open && (
                  <div className="mt-3 border-t border-[var(--border-color)] pt-3">
                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                      Enquiries this period ({m.enquiries.length})
                    </div>
                    {m.enquiries.length === 0 ? (
                      <div className="py-3 text-center text-xs text-[var(--text-muted)]">No enquiries in this period.</div>
                    ) : (
                      <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
                        {m.enquiries.map((e) => (
                          <div key={e.id} className="rounded-md border border-[var(--border-color)] bg-[var(--bg-soft)] px-2.5 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate text-xs font-semibold text-[var(--text-primary)]">{e.customer}</span>
                              <Badge tone={STATUS_TONE[e.status]}>{e.status}</Badge>
                            </div>
                            <div className="mt-0.5 flex items-center justify-between gap-2 text-[11px] text-[var(--text-muted)]">
                              <span className="truncate">{fmtFullDay(e.date)}{e.item ? ` · ${e.item}` : ''}</span>
                              <span className="flex-shrink-0 font-semibold text-accent">{e.valueInr ? `₹${formatINR(e.valueInr)}` : '—'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
/** One figure in a team card's stats strip; zeros fade to muted. */
function TeamFigure({ label, value, color, money }: { label: string; value: number; color?: string; money?: boolean }) {
  return (
    <div className="min-w-0 px-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="mt-0.5 truncate text-base font-extrabold tabular-nums" style={{ color: value ? (color ?? 'var(--text-primary)') : 'var(--text-muted)' }}>
        {money ? `₹${formatINR(value) || 0}` : value}
      </div>
    </div>
  );
}

// ---------- Accounts ----------
const ACCOUNTS_PAGE_SIZE = 20;
function AccountsTab({ rows }: { rows: Enquiry[] }) {
  const allRows = useMemo(() => accountsRollup(rows), [rows]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Search the full list, then paginate the results.
  const q = search.trim().toLowerCase();
  const all = useMemo(() => (q ? allRows.filter((a) => a.name.toLowerCase().includes(q)) : allRows), [allRows, q]);
  const totalPages = Math.max(1, Math.ceil(all.length / ACCOUNTS_PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageRows = all.slice((current - 1) * ACCOUNTS_PAGE_SIZE, current * ACCOUNTS_PAGE_SIZE);
  return (
    <div className="space-y-3">
      <div className="relative w-full sm:max-w-xs">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search accounts…"
          aria-label="Search accounts"
          className="!pl-9"
        />
      </div>
      <Table<AccountRow>
        title="Accounts — grouped by customer & customer type"
        count={`${all.length} account${all.length !== 1 ? 's' : ''}`}
        rows={pageRows}
        rowKey={(a) => a.name}
        empty={q ? 'No accounts match your search' : 'No accounts in this period'}
        columns={[
          { key: 'name', header: 'Customer', cell: (a) => <span className="font-bold">{a.customer}</span>, minWidth: '220px' },
          { key: 'category', header: 'Customer Type', cell: (a) => (a.category ? <Badge tone="accent">{a.category}</Badge> : <span style={{ color: 'var(--text-muted)' }}>—</span>) },
          { key: 'orders', header: 'Enquiries', cell: (a) => a.orders },
          { key: 'so', header: 'Sales Orders', cell: (a) => <span style={{ color: '#22c55e' }}>{a.so}</span> },
          { key: 'value', header: 'Order Value', cell: (a) => formatINR(a.value) },
        ]}
        footer={
          all.length > ACCOUNTS_PAGE_SIZE ? (
            <div className="flex items-center justify-between">
              <span>
                Showing {(current - 1) * ACCOUNTS_PAGE_SIZE + 1}–{Math.min(current * ACCOUNTS_PAGE_SIZE, all.length)} of {all.length}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="glass" size="sm" disabled={current <= 1} onClick={() => setPage(current - 1)}>Prev</Button>
                <span>Page {current} / {totalPages}</span>
                <Button variant="glass" size="sm" disabled={current >= totalPages} onClick={() => setPage(current + 1)}>Next</Button>
              </div>
            </div>
          ) : undefined
        }
      />
    </div>
  );
}
